import { Autocomplete, Box, CircularProgress, ListItem, ListItemText, TextField, useTheme } from '@mui/material';
import { createFilterOptions } from '@mui/material/Autocomplete';
import { WorkflowExecutionSummary } from 'api/model';
import { useMemo } from 'react';
import { formatDuration, toLocaleDateStr } from 'utils/helpers';
import { RevisionChip } from 'components/table/RevisionChip';
import { HintChip, LatestChip } from './ExecutionSelect.styles';

type ExecutionSelectProps = {
  workflowId?: string;
  label?: string;
  options: WorkflowExecutionSummary[];
  latest?: WorkflowExecutionSummary;
  value?: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  required?: boolean;
  loading?: boolean;
  showDefaultLast?: boolean;
};

const ExecutionSelect = ({
  workflowId,
  label = 'Execution',
  options,
  latest,
  value,
  onChange,
  disabled,
  required,
  loading,
  showDefaultLast = false,
}: ExecutionSelectProps) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const greenColor = isDarkMode ? theme.palette.success.light : theme.palette.success.dark;
  const grayColor = theme.palette.text.secondary;

  const optionsList = useMemo(() => {
    if (latest && !options.find((execution) => execution.id === latest.id)) {
      return [latest, ...options];
    }
    return options;
  }, [options, latest]);

  const selected = optionsList.find((r) => r.id === value) ?? null;

  const filterOptions = useMemo(
    () =>
      createFilterOptions<WorkflowExecutionSummary>({
        matchFrom: 'any',
        ignoreCase: true,
        trim: true,
        stringify: (opt) => {
          const parts: string[] = [opt.id];
          if (opt.trigger_time) {
            const d = new Date(opt.trigger_time);
            parts.push(toLocaleDateStr(opt.trigger_time) ?? '-');
            parts.push(d.toISOString());
          }
          return parts.join(' ');
        },
      }),
    [],
  );

  return (
    <Autocomplete
      options={optionsList}
      filterOptions={filterOptions}
      getOptionLabel={(opt) => opt?.id ?? ''}
      value={selected}
      onChange={(_, opt) => onChange(opt?.id ?? '')}
      loading={loading}
      disabled={disabled || !workflowId}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          required={required}
          placeholder={workflowId ? 'Search executions…' : 'Select workflow first'}
          InputProps={{
            ...params.InputProps,
            startAdornment: selected ? (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.75,
                  pl: 1,
                  pr: 0,
                  width: 'auto',
                  overflow: 'hidden',
                  pointerEvents: 'none',
                }}
              >
                <RevisionChip revision={selected.workflow_revision ?? 1} size="small" />

                <span
                  className="text-sm"
                  style={{
                    flex: 1,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    color: greenColor,
                  }}
                >
                  {toLocaleDateStr(selected.trigger_time) ?? '-'} ({formatDuration(selected.duration)})
                </span>
              </Box>
            ) : (
              <>{showDefaultLast && <HintChip className="ml-2" size="small" label="Last Execution" />}</>
            ),

            endAdornment: (
              <>
                {loading && <CircularProgress size={18} />}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      renderOption={(propsMUI, opt) => (
        <ListItem
          {...propsMUI}
          key={opt.id}
          dense
          sx={{
            gap: 1,
            color: 'text.primary',
          }}
        >
          <ListItemText
            primary={
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  width: '100%',
                  color: 'text.primary',
                }}
              >
                <RevisionChip revision={opt.workflow_revision ?? 1} size="small" />

                <span
                  className="mr-2 flex-1 "
                  style={{
                    color: greenColor,
                  }}
                >
                  {toLocaleDateStr(opt.trigger_time) ?? '-'} ({formatDuration(opt.duration)})
                </span>
                {opt.id === latest?.id && <LatestChip className="ml-4" size="small" label="latest" />}
                <code style={{ opacity: 0.8, color: grayColor }}>{opt.id.slice(0, 8)}…</code>
              </Box>
            }
          />
        </ListItem>
      )}
      renderTags={() => null}
      isOptionEqualToValue={(a, b) => a.id === b.id}
    />
  );
};

export default ExecutionSelect;
